<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRoute } from 'vue-router';
import api from '@/lib/api.js';
import * as XLSX from 'xlsx';

const props = defineProps({
  schoolType: {
    type: String,
    default: null
  }
});

const route = useRoute();

// 학교 결과 데이터
const schoolData = ref(null);
const loading = ref(true);

// API 호출 함수
const fetchSchoolResult = async () => {
  try {
    loading.value = true;
    const { exam_id, schul_code } = route.query;
    
    if (!exam_id || !schul_code) {
      throw new Error('필수 파라미터가 없습니다.');
    }

    console.log('Analysis - School 결과 API 요청:', {
      exam_id,
      schul_code
    });

    const response = await api.post('/exam/tea/resultSchoolDetail', {
      exam_id,
      schul_code
    });

    if (response.data.success) {
      schoolData.value = response.data.result;
      console.log('Analysis - School 결과 데이터:', schoolData.value);
    } else {
      throw new Error(response.data.message || '데이터를 가져오는데 실패했습니다.');
    }
  } catch (error) {
    console.error('Analysis - School 결과 데이터 조회 실패:', error);
  } finally {
    loading.value = false;
  }
};

// API 데이터 기반으로 allGradesAnalysis 생성
const allGradesAnalysis = computed(() => {
  if (!schoolData.value?.classList || !schoolData.value?.totalList) {
    return {};
  }

  const { classList, totalList } = schoolData.value;
  const result = {};

  // 학년별로 그룹화
  const gradeGroups = {};
  
  classList.forEach(classItem => {
    const gradeCode = classItem.grade_code;
    if (!gradeGroups[gradeCode]) {
      gradeGroups[gradeCode] = [];
    }
    gradeGroups[gradeCode].push(classItem);
  });

  // 각 학년별로 데이터 처리
  Object.keys(gradeGroups).forEach(gradeCode => {
    const gradeClasses = gradeGroups[gradeCode];
    
    // school_cls로 정렬 (문자열 기준으로 자연 정렬)
    gradeClasses.sort((a, b) => {
      const aNum = parseInt(a.school_cls) || 0;
      const bNum = parseInt(b.school_cls) || 0;
      return aNum - bNum;
    });

    // 해당 학년의 totalList 데이터 찾기
    const gradeTotalData = totalList.find(item => item.grade_code === gradeCode);
    
    const gradeAnalysis = [];

    // 전체 데이터 추가 (classList 합산으로)
    if (gradeClasses.length > 0) {
      // 각 반 데이터를 합산
      const totalLit = {
        examinees: 0,
        originalSum: 0,
        convertedSum: 0,
        level1: 0,
        level2: 0,
        level3: 0,
        level4: 0
      };
      
      const totalNum = {
        examinees: 0,
        originalSum: 0,
        convertedSum: 0,
        level1: 0,
        level2: 0,
        level3: 0,
        level4: 0
      };
      
      gradeClasses.forEach(classItem => {
        // female과 male 데이터 합치기 (male은 기존 key 사용)
        const getMergedValue = (field) => {
          const femaleValue = classItem[`female_${field}`] || 0;
          const maleValue = classItem[field] || 0; // male은 기존 키 사용
          
          // female + male 합산
          return femaleValue + maleValue;
        };


        
        // 문해력 합산 (female + male)
        const litLevel1 = getMergedValue('korlevel1');
        const litLevel2 = getMergedValue('korlevel2');
        const litLevel3 = getMergedValue('korlevel3');
        const litLevel4 = getMergedValue('korlevel4');
        const litExaminees = litLevel1 + litLevel2 + litLevel3 + litLevel4;
        // 점수는 API에서 받은 값 그대로 사용 (male 값)
        const litScoreOrigin = classItem.korscoreorigin || 0;
        const litScore = classItem.korscore || 0;
        
        totalLit.examinees += litExaminees;
        totalLit.originalSum += litScoreOrigin * litExaminees;
        totalLit.convertedSum += litScore * litExaminees;
        totalLit.level1 += litLevel1;
        totalLit.level2 += litLevel2;
        totalLit.level3 += litLevel3;
        totalLit.level4 += litLevel4;
        
        // 수리력 합산 (female + male)
        const numLevel1 = getMergedValue('matlevel1');
        const numLevel2 = getMergedValue('matlevel2');
        const numLevel3 = getMergedValue('matlevel3');
        const numLevel4 = getMergedValue('matlevel4');
        const numExaminees = numLevel1 + numLevel2 + numLevel3 + numLevel4;
        // 점수는 API에서 받은 값 그대로 사용 (male 값)
        const numScoreOrigin = classItem.matscoreorigin || 0;
        const numScore = classItem.matscore || 0;
        
        totalNum.examinees += numExaminees;
        totalNum.originalSum += numScoreOrigin * numExaminees;
        totalNum.convertedSum += numScore * numExaminees;
        totalNum.level1 += numLevel1;
        totalNum.level2 += numLevel2;
        totalNum.level3 += numLevel3;
        totalNum.level4 += numLevel4;
      });
      
      gradeAnalysis.push({
        class: '전체',
        lit: {
          examinees: totalLit.examinees > 0 ? totalLit.examinees : '',
          originalAvg: totalLit.examinees > 0 ? totalLit.originalSum / totalLit.examinees : '',
          convertedAvg: totalLit.examinees > 0 ? totalLit.convertedSum / totalLit.examinees : '',
          level1: totalLit.examinees > 0 ? totalLit.level1 : '',
          level2: totalLit.examinees > 0 ? totalLit.level2 : '',
          level3: totalLit.examinees > 0 ? totalLit.level3 : '',
          level4: totalLit.examinees > 0 ? totalLit.level4 : ''
        },
        num: {
          examinees: totalNum.examinees > 0 ? totalNum.examinees : '',
          originalAvg: totalNum.examinees > 0 ? totalNum.originalSum / totalNum.examinees : '',
          convertedAvg: totalNum.examinees > 0 ? totalNum.convertedSum / totalNum.examinees : '',
          level1: totalNum.examinees > 0 ? totalNum.level1 : '',
          level2: totalNum.examinees > 0 ? totalNum.level2 : '',
          level3: totalNum.examinees > 0 ? totalNum.level3 : '',
          level4: totalNum.examinees > 0 ? totalNum.level4 : ''
        }
      });
    }

    // 각 반 데이터 추가
    gradeClasses.forEach(classItem => {
      const gradeText = classItem.schul_type === 'E' ? 
        `${classItem.grade_code}-${classItem.school_cls}` :
        classItem.schul_type === 'M' ? 
        `${classItem.grade_code}-${classItem.school_cls}` :
        `${classItem.grade_code}-${classItem.school_cls}`;

      // female과 male 데이터 합치기 (male은 기존 key 사용)
      const getMergedValue = (field) => {
        const femaleValue = classItem[`female_${field}`] || 0;
        const maleValue = classItem[field] || 0; // male은 기존 키 사용
        
        // female + male 합산
        return femaleValue + maleValue;
      };



      // 문해력 데이터 합산
      const litLevel1 = getMergedValue('korlevel1');
      const litLevel2 = getMergedValue('korlevel2');
      const litLevel3 = getMergedValue('korlevel3');
      const litLevel4 = getMergedValue('korlevel4');
      const litExaminees = litLevel1 + litLevel2 + litLevel3 + litLevel4;
      
      // 수리력 데이터 합산
      const numLevel1 = getMergedValue('matlevel1');
      const numLevel2 = getMergedValue('matlevel2');
      const numLevel3 = getMergedValue('matlevel3');
      const numLevel4 = getMergedValue('matlevel4');
      const numExaminees = numLevel1 + numLevel2 + numLevel3 + numLevel4;

      gradeAnalysis.push({
        class: gradeText,
        lit: {
          examinees: litExaminees > 0 ? litExaminees : '',
          originalAvg: litExaminees > 0 ? (classItem.korscoreorigin || '') : '',
          convertedAvg: litExaminees > 0 ? (classItem.korscore || '') : '',
          level1: litExaminees > 0 ? litLevel1 : '',
          level2: litExaminees > 0 ? litLevel2 : '',
          level3: litExaminees > 0 ? litLevel3 : '',
          level4: litExaminees > 0 ? litLevel4 : ''
        },
        num: {
          examinees: numExaminees > 0 ? numExaminees : '',
          originalAvg: numExaminees > 0 ? (classItem.matscoreorigin || '') : '',
          convertedAvg: numExaminees > 0 ? (classItem.matscore || '') : '',
          level1: numExaminees > 0 ? numLevel1 : '',
          level2: numExaminees > 0 ? numLevel2 : '',
          level3: numExaminees > 0 ? numLevel3 : '',
          level4: numExaminees > 0 ? numLevel4 : ''
        }
      });
    });

    result[gradeCode] = gradeAnalysis;
  });

  console.log('생성된 allGradesAnalysis:', result);
  console.log('allGradesAnalysis keys:', Object.keys(result));
  return result;
});

const selectedGrade = ref(null); 

// 학교 타입에 따른 학년 옵션 생성
const gradeOptions = computed(() => {
  if (!schoolData.value?.totalList) return [];
  
  const grades = schoolData.value.totalList.map(item => {
    const gradeCode = item.grade_code;
    let label = '';
    
    if (schoolData.value.schul_type === 'E') { // 초등학교
      label = `${gradeCode}학년`;
    } else if (schoolData.value.schul_type === 'M') { // 중학교
      label = `중${gradeCode}학년`;
    } else if (schoolData.value.schul_type === 'H') { // 고등학교
      label = `고${gradeCode}학년`;
    } else {
      label = `${gradeCode}학년`;
    }
    
    return {
      label,
      value: gradeCode
    };
  });

  console.log('gradeOptions:', grades);

  // selectedGrade 초기값 설정
  if (grades.length > 0 && selectedGrade.value === null) {
    selectedGrade.value = grades[0].value;
    console.log('selectedGrade 초기값 설정:', selectedGrade.value);
  }

  return grades;
});

const filteredClassAnalysis = computed(() => {
  console.log('filteredClassAnalysis - selectedGrade:', selectedGrade.value);
  console.log('filteredClassAnalysis - allGradesAnalysis keys:', Object.keys(allGradesAnalysis.value));
  console.log('filteredClassAnalysis - 선택된 데이터:', allGradesAnalysis.value[selectedGrade.value]);
  
  if (selectedGrade.value && allGradesAnalysis.value[selectedGrade.value]) {
    return allGradesAnalysis.value[selectedGrade.value];
  } 
  return []; 
});

// schoolData 변경 감지하여 selectedGrade 초기값 설정
watch(schoolData, (newData) => {
  if (newData && newData.totalList && newData.totalList.length > 0 && selectedGrade.value === null) {
    selectedGrade.value = newData.totalList[0].grade_code;
    console.log('watch에서 selectedGrade 설정:', selectedGrade.value);
  }
}, { immediate: true });

// 엑셀 다운로드 함수
const downloadExcel = () => {
  if (!schoolData.value || !allGradesAnalysis.value) {
    alert('다운로드할 데이터가 없습니다.');
    return;
  }

  const workbook = XLSX.utils.book_new();
  
  // 각 학년별로 시트 생성
  Object.keys(allGradesAnalysis.value).forEach(gradeCode => {
    const gradeData = allGradesAnalysis.value[gradeCode];
    
    // 학년 라벨 생성
    let gradeLabel = '';
    if (schoolData.value.schul_type === 'E') {
      gradeLabel = `${gradeCode}학년`;
    } else if (schoolData.value.schul_type === 'M') {
      gradeLabel = `중${gradeCode}학년`;
    } else if (schoolData.value.schul_type === 'H') {
      gradeLabel = `고${gradeCode}학년`;
    } else {
      gradeLabel = `${gradeCode}학년`;
    }

    // 헤더 생성
    const headers = [
      '학교코드', '학교명', '학년', '반', '검사형', 
      '문해력응시자', '문해력1수준', '문해력2수준', '문해력3수준', '문해력4수준', 
      '문해력 원점수 평균', '문해력 변환점수 평균',
      '수리력응시자', '수리력1수준', '수리력2수준', '수리력3수준', '수리력4수준',
      '수리력 원점수 평균', '수리력 변환점수 평균'
    ];

    // 데이터 생성
    const excelData = gradeData.map((item, index) => {
      // 해당 반의 실제 데이터를 찾기 (전체가 아닌 경우)
      let classData = null;
      if (item.class !== '전체') {
        // "4-1" 형태에서 반 번호 추출
        const classNumber = item.class.split('-').pop();
        classData = schoolData.value.classList.find(c => 
          c.grade_code === gradeCode && c.school_cls === classNumber
        );
      } else {
        // 전체인 경우 해당 학년의 첫 번째 반 데이터 사용
        classData = schoolData.value.classList.find(c => c.grade_code === gradeCode);
      }
      
      const schoolName = classData?.schul_nm || schoolData.value?.schul_nm || '';
      const schoolCode = classData?.omr_code || '';
      const paperType = classData?.paper_type || '';
      
      // 반 데이터에서 학년 부분 제거 (예: "4-1" → "1", "전체" → "전체")
      const className = item.class === '전체' ? '전체' : item.class.split('-').pop();
      
      return [
        schoolCode,
        schoolName,
        gradeCode,
        className,
        paperType,
        item.lit.examinees,
        item.lit.level1,
        item.lit.level2,
        item.lit.level3,
        item.lit.level4,
        typeof item.lit.originalAvg === 'number' ? item.lit.originalAvg.toFixed(2) : item.lit.originalAvg,
        typeof item.lit.convertedAvg === 'number' ? item.lit.convertedAvg.toFixed(2) : item.lit.convertedAvg,
        item.num.examinees,
        item.num.level1,
        item.num.level2,
        item.num.level3,
        item.num.level4,
        typeof item.num.originalAvg === 'number' ? item.num.originalAvg.toFixed(2) : item.num.originalAvg,
        typeof item.num.convertedAvg === 'number' ? item.num.convertedAvg.toFixed(2) : item.num.convertedAvg
      ];
    });

    // 헤더와 데이터 결합
    const sheetData = [headers, ...excelData];
    
    // 워크시트 생성
    const worksheet = XLSX.utils.aoa_to_sheet(sheetData);
    
    // 시트명 설정 (특수문자 제거)
    const sheetName = gradeLabel.replace(/[\\\/\[\]:\*\?]/g, '');
    
    // 워크북에 시트 추가
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
  });

  // 파일명 생성
  const schoolName = schoolData.value.schul_nm || '학교';
  const today = new Date();
  const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '');
  const fileName = `${schoolName}_반별진단결과_${dateStr}.xlsx`;

  // 엑셀 파일 다운로드
  XLSX.writeFile(workbook, fileName);
};

onMounted(() => {
  fetchSchoolResult();
});
</script>

<template>
  <div class="table-container">
    <div class="table-top">
      <div class="title pb-4">
        <h5>우리 학교 반별 진단 결과</h5>
        <p class="download-button" @click="downloadExcel">반별 진단 결과 엑셀 다운로드</p>
      </div>   
      <div class="tabs" v-if="gradeOptions.length > 1">
        <p
          v-for="gradeOpt in gradeOptions"
          :key="gradeOpt.value"
          @click="selectedGrade = gradeOpt.value"
          :class="{ 'selected': selectedGrade === gradeOpt.value }"
        >
          {{ gradeOpt.label }}
        </p>
      </div>         
    </div>
    <table>
      <thead>
        <tr>
          <th rowspan="3">반</th>
          <th colspan="3">문해력</th>
          <th colspan="4">문해력 수준별 학생 수</th>
          <th colspan="3">수리력</th>
          <th colspan="4">수리력 수준별 학생 수</th>
        </tr>
        <tr>
          <th>응시자</th>
          <th>원점수 평균</th>
          <th>변환점수 평균</th>          
          <th>1수준</th>
          <th>2수준</th>
          <th>3수준</th>
          <th>4수준</th>  
          <th>응시자</th>
          <th>원점수 평균</th>
          <th>변환점수 평균</th>          
          <th>1수준</th>
          <th>2수준</th>
          <th>3수준</th>
          <th>4수준</th>  
        </tr>        
      </thead>
      <tbody>
        <tr v-if="loading">
          <td colspan="15" class="text-center">로딩 중...</td>
        </tr>
        <tr v-else-if="filteredClassAnalysis.length === 0">
          <td colspan="15" class="text-center">데이터가 없습니다.</td>
        </tr>
        <tr v-else v-for="(item, index) in filteredClassAnalysis" :key="index">
          <td>{{ item.class }}</td>
          <td>{{ item.lit.examinees }}</td>
          <td>{{ typeof item.lit.originalAvg === 'number' ? item.lit.originalAvg.toFixed(2) : item.lit.originalAvg }}</td>
          <td>{{ typeof item.lit.convertedAvg === 'number' ? item.lit.convertedAvg.toFixed(2) : item.lit.convertedAvg }}</td>
          <td>{{ item.lit.level1 }}</td>
          <td>{{ item.lit.level2 }}</td>
          <td>{{ item.lit.level3 }}</td>
          <td>{{ item.lit.level4 }}</td>
          <td>{{ item.num.examinees }}</td>
          <td>{{ typeof item.num.originalAvg === 'number' ? item.num.originalAvg.toFixed(2) : item.num.originalAvg }}</td>
          <td>{{ typeof item.num.convertedAvg === 'number' ? item.num.convertedAvg.toFixed(2) : item.num.convertedAvg }}</td>
          <td>{{ item.num.level1 }}</td>
          <td>{{ item.num.level2 }}</td>
          <td>{{ item.num.level3 }}</td>
          <td>{{ item.num.level4 }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style lang="scss" scoped>
.table-container {
    background-color: #fff;
    padding: 20px 20px 30px 20px;
    border-radius: 15px;
}
.table-top {
  display: flex;
  flex-direction: column;
  .title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
  .download-button {
    background-color: var(--smoe-primary-500);
    color: #fff;
    border-radius: 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 0 10px;
    cursor: pointer;
    font-size: 16px;
    font-weight: 500;
    height: 40px;
    line-height: 40px;
  } 
  .tabs {
    display: flex;
    margin-bottom: 15px;
    border-bottom: 1px solid #333;
    font-size: 16px;
    p {
      padding: 4px 24px;     
      cursor: pointer;
      &.selected {        
       position: relative;   
       border: 1px solid #333; 
       &::after {
        content: '';
        position: absolute;
        left: 0;
        bottom: -2px;
        width: 100%;
        height: 2px;
        background: #fff;
       }  
      }       
    }
  }
}

/* 테이블 기본 스타일 */
table {
  width: 100%;
  border-collapse: collapse;
  text-align: center;
  font-size: 16px;
}
th, td {
  border: 1px solid #ccc; padding: 8px 4px; vertical-align: middle;
}
th {
  background-color: #f2f2f2; font-weight: 500;
}
td {
  text-align: right;

  &:first-child { 
    text-align: center;
  }
}

/* 반복문으로 컬럼 너비 처리 (총 15개) */
@for $i from 1 through 15 { 
  @if $i == 1 {
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; } // 반
  } 
  // 문해력 응시자, 원점수, 변환점수, 수준 4개
  @else if $i == 2 or $i == 8 { // 응시자
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 60px; }
  } @else if $i == 3 or $i == 9 { // 원점수 평균
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 70px; }
  } @else if $i == 4 or $i == 10 { // 변환점수 평균
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 70px; }
  } @else if $i >= 5 and $i <= 7 { // 문해력 수준 1~3
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; }
  } @else if $i == 8 { // 문해력 수준 4 (윗 조건에 걸림)
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; }
  }
  // 수리력 응시자, 원점수, 변환점수, 수준 4개
  // 위에서 지정된 컬럼 순서 (8번은 수리력 응시자, 9번 원점수, 10번 변환점수, 11~14 수준)
  @else if $i >= 11 and $i <= 14 { // 수리력 수준 1~4
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; }
  } @else if $i == 15 { // 최종 수리력 4수준
    th:nth-child(#{$i}), td:nth-child(#{$i}) { width: 50px; }
  }
}

// 컬럼 색상
$literacy-color: var(--result-light-blue);
$numeracy-color: var( --result-light-red);

@for $i from 2 through 3 {
  tr:nth-child(1) th:nth-child(#{$i}) {color: $literacy-color;}
}
@for $i from 1 through 7 {
  tr:nth-child(2) th:nth-child(#{$i}) {color: $literacy-color;}
}
@for $i from 4 through 5 {
  tr:nth-child(1) th:nth-child(#{$i}) {color: $numeracy-color;}
}
@for $i from 8 through 15 {
  tr:nth-child(2) th:nth-child(#{$i}) {color: $numeracy-color; }
}
</style>